import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import DietsFreemiumAccessQuery from './DietsFreemiumAccessQuery';
import { DietFreemiumAccess, DietsFreemiumAccessResult } from '../types';

type result = {
  loading: boolean;
  accessByDietId: Record<number, DietFreemiumAccess>;
};

interface IDietsFreemiumAccessConnectorApolloProps {
  children: (result: result) => React.ReactElement;
}

/**
 * Freemium availability of every diet, keyed by the backend diet id so callers
 * can look it up straight from their local diet config.
 *
 * The map is empty for a viewer whose diet access is still valid — they have
 * already paid, so there is nothing to weigh up between free and paid diets.
 *
 * Deliberately never throws: a missing or failing field leaves the map empty,
 * which renders no tags rather than taking down the settings wizard.
 */
export const DietsFreemiumAccessConnectorApollo: React.FC<
  IDietsFreemiumAccessConnectorApolloProps
> = ({ children }) => {
  const { loading, data } = useQuery<DietsFreemiumAccessResult>(DietsFreemiumAccessQuery, {
    // cache-and-network, so tags disappear on the next visit after a purchase
    // instead of hanging around on a cached freemium answer
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const accessByDietId = useMemo(() => {
    // Same access check the tracker and user meals already do: the catalogue is
    // identical for everyone, so only the viewer's own access can gate the tags.
    const todayDateString = new Date().toISOString().slice(0, 10);
    const dietAccessTo = data?.me?.diet?.accessTo;
    const hasDietAccess = Boolean(dietAccessTo && dietAccessTo >= todayDateString);

    if (hasDietAccess) {
      return {};
    }

    const diets = data?.diet?.diets ?? [];

    return diets.reduce<Record<number, DietFreemiumAccess>>((acc, diet) => {
      if (diet?.freemiumAccess) {
        acc[diet.id] = diet.freemiumAccess;
      }
      return acc;
    }, {});
  }, [data]);

  return children({ loading, accessByDietId });
};

export default DietsFreemiumAccessConnectorApollo;
